// Growem — premium timer audio. Synthesized with Web Audio (no assets), one distinct, musical
// cue per rest-timer milestone. Soft sine/triangle tones with an octave shimmer, gentle attack,
// exponential decay, and a low-pass so nothing is harsh. All calls are safe no-ops if audio is
// unavailable or blocked. The AudioContext unlocks on the set-mark tap that starts each rest.

let ac: AudioContext | null = null
function ctx(): AudioContext | null {
  try {
    if (!ac) {
      const C = window.AudioContext || (window as any).webkitAudioContext
      if (!C) return null
      ac = new C()
    }
    if (ac.state === 'suspended') void ac.resume()
    return ac
  } catch {
    return null
  }
}

interface Note { f: number; t: number; d: number; g?: number; type?: OscillatorType }

function play(notes: Note[]): void {
  const a = ctx()
  if (!a) return
  try {
    const now = a.currentTime
    const master = a.createGain()
    master.gain.value = 0.9
    const lp = a.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 5200
    master.connect(lp)
    lp.connect(a.destination)

    for (const n of notes) {
      const t0 = now + n.t
      const peak = n.g ?? 0.16
      // fundamental
      const o = a.createOscillator()
      o.type = n.type ?? 'sine'
      o.frequency.value = n.f
      const g = a.createGain()
      g.gain.setValueAtTime(0.0001, t0)
      g.gain.exponentialRampToValueAtTime(peak, t0 + 0.012)
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.d)
      o.connect(g); g.connect(master)
      o.start(t0); o.stop(t0 + n.d + 0.03)
      // octave shimmer for a richer, "premium" body
      const o2 = a.createOscillator()
      o2.type = 'sine'
      o2.frequency.value = n.f * 2
      o2.detune.value = 5
      const g2 = a.createGain()
      g2.gain.setValueAtTime(0.0001, t0)
      g2.gain.exponentialRampToValueAtTime(peak * 0.22, t0 + 0.012)
      g2.gain.exponentialRampToValueAtTime(0.0001, t0 + n.d * 0.8)
      o2.connect(g2); g2.connect(master)
      o2.start(t0); o2.stop(t0 + n.d + 0.03)
    }
  } catch {
    /* audio not available */
  }
}

// Notes (Hz). Frequencies chosen to be musical and mutually distinct.
const D5 = 587.33, A5 = 880, D6 = 1174.66
const C5 = 523.25, E5 = 659.25, G5 = 783.99, C6 = 1046.5
const F4 = 349.23, Gs4 = 415.30, Ds4 = 311.13

/** Rest begins — subtle low note. */
export function soundStart(): void {
  play([{ f: F4, t: 0, d: 0.13, g: 0.09, type: 'triangle' }])
}

/** Crossed the MINIMUM — the "green light": a bright rising fifth. */
export function soundMin(): void {
  play([{ f: D5, t: 0, d: 0.14, g: 0.16 }, { f: A5, t: 0.08, d: 0.24, g: 0.17 }])
}

/** Reached the IDEAL — a single soft shimmer at the sweet spot. */
export function soundIdeal(): void {
  play([{ f: D6, t: 0, d: 0.26, g: 0.12 }])
}

/** Passed the MAXIMUM — a calm descending pair, deliberately gentle (no alarm). */
export function soundMax(): void {
  play([{ f: Gs4, t: 0, d: 0.16, g: 0.11, type: 'triangle' }, { f: Ds4, t: 0.13, d: 0.34, g: 0.11, type: 'triangle' }])
}

/** Listo / recorded — a resolved major arpeggio, satisfying. */
export function soundDone(): void {
  play([
    { f: C5, t: 0, d: 0.13, g: 0.15 },
    { f: E5, t: 0.06, d: 0.13, g: 0.16 },
    { f: G5, t: 0.12, d: 0.14, g: 0.16 },
    { f: C6, t: 0.19, d: 0.4, g: 0.17 },
  ])
}
